import React, { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  formPanelStatusOf,
  LabelRow,
  TwoColumnRow,
} from "../../FormPanel";
import { FormInput, FormMoneyInput, InputDate, SelectForm } from "../../Input";
import { notify } from "../../Notification";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../PopUp/confirmShared";
import { ErrorResponse, getErrorMessages } from "../../../services";
import { useGetAccountsQuery } from "../../../services/accounts/accountsApi";
import { useGetCategoriesQuery } from "../../../services/categories/categoriesApi";
import { useCreateTransactionMutation } from "../../../services/transactions/transactionsApi";
import {
  DateFormat,
  formatCurrency,
  formatDate,
  TransactionType,
} from "../../../utils";
import {
  cashbackCategoryOf,
  cashbackSourceAccountsOf,
} from "../../../utils/cashback";
import { localToday, toIsoDate } from "../../../utils/dates";
import {
  ACCOUNT_RULES,
  ADD_CASHBACK_HELPER,
  ADD_CASHBACK_TITLE,
  AMOUNT_RULES,
  createDefaultValues,
  DATE_RULES,
  FORM_ID,
  MISSING_CATEGORY_ERROR,
  NOTE_RULES,
} from "./constants";
import { MobileAddCashbackFields } from "./MobileAddCashbackFields";
import { AddCashbackFormValues, AddCashbackPanelProps } from "./types";

export const AddCashbackPanel: React.FC<AddCashbackPanelProps> = ({
  accountId,
  isOpen,
  onClose,
}) => {
  const [panelError, setPanelError] = useState<string | null>(null);
  const [startValues] = useState(createDefaultValues);
  const [createTransaction] = useCreateTransactionMutation();
  const accountsQuery = useGetAccountsQuery();
  const categoriesQuery = useGetCategoriesQuery(true);
  const form = useForm<AddCashbackFormValues>({
    defaultValues: startValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, register, reset, setValue } = form;
  const { isSubmitting } = useFormState({ control });

  const accountOptions = useMemo(
    () =>
      cashbackSourceAccountsOf(accountsQuery.data).map((account) => ({
        value: String(account.id),
        label: account.name,
      })),
    [accountsQuery.data],
  );
  const currentAccount = accountOptions.find(
    (option) => option.value === String(accountId),
  );
  const cashbackCategory = cashbackCategoryOf(categoriesQuery.data);
  const isCategoryMissing =
    categoriesQuery.data !== undefined && !cashbackCategory;

  // A reset, not resetField: the field isn't mounted while the panel still loads.
  useEffect(() => {
    if (currentAccount && getValues("account") === null) {
      reset(
        { ...getValues(), account: currentAccount },
        { keepDirtyValues: true },
      );
    }
  }, [currentAccount, getValues, reset]);

  const isLoadMissing =
    accountsQuery.data === undefined || categoriesQuery.data === undefined;
  const load = {
    status: formPanelStatusOf({
      isLoading:
        isLoadMissing &&
        (accountsQuery.isFetching || categoriesQuery.isFetching),
      isError:
        isLoadMissing && (accountsQuery.isError || categoriesQuery.isError),
    }),
    onRetry: () => {
      void accountsQuery.refetch();
      void categoriesQuery.refetch();
    },
    loadingLabel: "Loading accounts…",
  };

  const onSubmit: SubmitHandler<AddCashbackFormValues> = async ({
    transactionDate,
    amount,
    account,
    note,
  }) => {
    if (!transactionDate || amount === "" || !account || !cashbackCategory) {
      return;
    }
    setPanelError(null);
    try {
      await createTransaction({
        transactionType: {
          value: TransactionType.INCOME,
          label: TransactionType.INCOME,
        },
        fromAccount: null,
        toAccount: account,
        amount: String(amount),
        transactionDate: toIsoDate(transactionDate),
        note: note.trim(),
        category: {
          value: cashbackCategory.value,
          label: cashbackCategory.label,
        },
        subCategory: null,
        isRecurring: false,
        frequency: null,
      }).unwrap();
      notify.success("Cashback added");
      onClose();
    } catch (error: unknown) {
      setPanelError(getErrorMessages(error as ErrorResponse).join(" "));
    }
  };

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();
    const amount =
      values.amount === "" ? undefined : formatCurrency(values.amount);
    return buildDraftDetails({
      noun: "cashback",
      fieldValues: [values.amount, values.transactionDate, values.note],
      amount,
      primaryLabel: joinDetailParts(
        values.account?.label,
        values.transactionDate
          ? formatDate(values.transactionDate, DateFormat.DMY_SPACE)
          : undefined,
      ),
    });
  };

  const error = isCategoryMissing ? MISSING_CATEGORY_ERROR : panelError;

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel={ADD_CASHBACK_TITLE}
      eyebrow="CASHBACK"
      title={ADD_CASHBACK_TITLE}
      form={form}
      resetValues={startValues}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Save"
      isSubmitDisabled={isCategoryMissing}
      error={error}
      load={load}
      discard={{
        title: "Discard this cashback?",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: ADD_CASHBACK_TITLE,
        submitLabel: "Save",
        fields: (
          <MobileAddCashbackFields
            control={control}
            register={register}
            setValue={setValue}
            accountOptions={accountOptions}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <FormCard>
        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="add-cashback-date">Date *</FieldLabel>
            </LabelRow>
            <InputDate
              id="add-cashback-date"
              name="transactionDate"
              control={control}
              rules={DATE_RULES}
              maxDate={localToday()}
              placeholder="YYYY/MM/DD"
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="add-cashback-amount">Amount *</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="add-cashback-amount"
              name="amount"
              control={control}
              rules={AMOUNT_RULES}
              placeholder="Enter Amount"
              disabled={isSubmitting}
              autoFocus
            />
          </Field>
        </TwoColumnRow>
        <FieldHelper>{ADD_CASHBACK_HELPER}</FieldHelper>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="add-cashback-account">Account *</FieldLabel>
          </LabelRow>
          <SelectForm
            id="add-cashback-account"
            testId="add-cashback-account"
            name="account"
            control={control}
            rules={ACCOUNT_RULES}
            options={accountOptions}
            disabled={isSubmitting}
          />
        </Field>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="add-cashback-note">Description</FieldLabel>
          </LabelRow>
          <FormInput
            id="add-cashback-note"
            name="note"
            control={control}
            rules={NOTE_RULES}
            handleClear={false}
            placeholder="Optional description"
            disabled={isSubmitting}
          />
        </Field>
      </FormCard>
    </FormPanel>
  );
};
